// zoomStageMath — the pure geometry behind ZoomStage (COMPONENTS §Catalog):
// where a picture sits inside its box at a given scale + pan, how a zoom keeps
// the point under the pointer still, and when a drag reads as a swipe. Pure so
// the stage's behaviour is unit-tested without a browser (the fit-engine
// precedent: `computeColumnFit`).

export interface Size {
  width: number;
  height: number;
}
export interface Point {
  x: number;
  y: number;
}

/** The picture's rendered size at scale 1: CONTAINED in the box (never cropped). */
export function fitContain(natural: Size, box: Size): Size {
  if (natural.width <= 0 || natural.height <= 0 || box.width <= 0 || box.height <= 0) return { width: 0, height: 0 };
  const ratio = Math.min(box.width / natural.width, box.height / natural.height);
  return { width: natural.width * ratio, height: natural.height * ratio };
}

export function clampScale(scale: number, min: number, max: number): number {
  if (!Number.isFinite(scale)) return min;
  return Math.min(max, Math.max(min, scale));
}

/** Keeps the picture covering the box: the pan is bounded by the overflow on
 *  each axis (half the excess each side); an axis with no overflow snaps to 0. */
export function clampPan(pan: Point, fitted: Size, box: Size, scale: number): Point {
  const maxX = Math.max(0, (fitted.width * scale - box.width) / 2);
  const maxY = Math.max(0, (fitted.height * scale - box.height) / 2);
  // `|| 0` folds a -0 (from clamping against a 0 bound) into a plain 0.
  return {
    x: Math.min(maxX, Math.max(-maxX, pan.x)) || 0,
    y: Math.min(maxY, Math.max(-maxY, pan.y)) || 0,
  };
}

/** Zoom by `factor` around `at` (box-relative, origin at the box centre): the
 *  picture point under the pointer stays under the pointer. Pan is in box px. */
export function zoomAround(
  state: { scale: number; pan: Point },
  factor: number,
  at: Point,
  bounds: { min: number; max: number; fitted: Size; box: Size },
): { scale: number; pan: Point } {
  const scale = clampScale(state.scale * factor, bounds.min, bounds.max);
  const ratio = scale / state.scale;
  const pan = { x: at.x - (at.x - state.pan.x) * ratio, y: at.y - (at.y - state.pan.y) * ratio };
  return { scale, pan: clampPan(pan, bounds.fitted, bounds.box, scale) };
}

export const SWIPE_THRESHOLD_PX = 56;

/** A horizontal drag at rest (scale 1) reads as a swipe once it clearly leads
 *  the vertical movement past the threshold; a zoomed picture never swipes
 *  (the drag is a pan). Returns the direction the person moved TOWARD. */
export function swipeIntent(dx: number, dy: number, scale: number, threshold = SWIPE_THRESHOLD_PX): 'prev' | 'next' | null {
  if (scale > 1.001) return null;
  if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.5) return null;
  return dx > 0 ? 'prev' : 'next';
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
export function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
